"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import { useUser } from "@clerk/nextjs";

import { RecurrentBody } from "@/backend/domains/payment-system/infrastructure/tiptop";

import { cn } from "@/lib/utils";
import { useOrganizationStore } from "@/stores/organization.store";
import delay from "@/utils/delay";

import { PaymentsCheckoutForm } from "../../../domain/types";
import { useTiptopStore } from "../domain/store";
import { TiptopPaymentBlocksResult } from "../domain/types";

//

const publicId = process.env.NEXT_PUBLIC_TIPTOP_PUBLIC_ID;

//

export const TiptopCheckoutForm: PaymentsCheckoutForm = ({
  className,
  invoice,
  buttonText,
  onSuccess,
  onFail,
  onInit,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const tiptop = useTiptopStore((s) => s.tiptop);
  const { user } = useUser();
  const organization = useOrganizationStore();
  const [isMounted, setMounted] = useState(false);
  const email = user?.primaryEmailAddress?.emailAddress;

  const data = invoice.data;

  const recurrent =
    data.type === "subscription"
      ? ({
          Interval: data.interval,
          Period: data.period,
          Amount: data.amount,
          StartDate: data.startDate,
        } satisfies RecurrentBody)
      : null;

  //

  const auth =
    (data.type === "subscription" && data.authorizingPayment) || data.type === "authorizing";

  const blocksApp = useMemo(
    () =>
      email
        ? new tiptop.PaymentBlocks(
            {
              currency: "KZT",
              amount: invoice.amount,
              description: invoice.description,
              invoiceId: invoice.id,
              data: recurrent ? { PaymentData: { recurrent } } : {},
              publicId,
              accountId: organization.id,
              email,
              requireEmail: false,
              language: "ru-RU",
              applePaySupport: false,
              googlePaySupport: false,
              auth: auth,
            },
            {
              appearance: {
                colors: {
                  primaryButtonColor: "#2e71fc",
                  primaryButtonTextColor: "#ffffff",
                  primaryHoverButtonColor: "#2e71fc",
                  primaryButtonHoverTextColor: "#ffffff",
                  activeInputColor: "#0b1e46",
                  inputBackground: "#ffffff",
                  inputColor: "#8c949f",
                  inputBorderColor: "#e2e8ef",
                  errorColor: "#eb5757",
                },
                borders: {
                  radius: "8px",
                },
              },
              components: {
                paymentButton: {
                  text: buttonText ?? "Оплатить",
                  fontSize: "16px",
                },
                paymentForm: {
                  labelFontSize: "16px",
                  activeLabelFontSize: "12px",
                  fontSize: "16px",
                },
              },
            },
          )
        : null,
    [
      auth,
      buttonText,
      email,
      invoice.amount,
      invoice.description,
      invoice.id,
      organization.id,
      recurrent,
      tiptop.PaymentBlocks,
    ],
  );

  //

  useEffect(() => {
    if (!blocksApp || blocksApp.mounted || !containerRef.current || isMounted) return;

    blocksApp.mount(containerRef.current);

    const handleInit = async () => {
      await delay(1500);
      setMounted(true);
      if (onInit) await onInit();
    };

    const handleDestroy = () => {
      console.log("destroy");
    };

    const handleSuccess = (result?: TiptopPaymentBlocksResult) => {
      if (onSuccess) return onSuccess({ transactionId: result?.data.transactionId });
    };

    const handleFail = (result?: TiptopPaymentBlocksResult) => {
      if (onFail) return onFail(result);
    };

    blocksApp.on("init", handleInit);
    blocksApp.on("destroy", handleDestroy);
    blocksApp.on("success", handleSuccess);
    blocksApp.on("fail", handleFail);
  }, [blocksApp, isMounted, onFail, onInit, onSuccess]);

  //

  return (
    <div className={cn("inset-y-0 flex gap-4", className)}>
      <div
        ref={containerRef}
        className={cn("relative -mb-10 min-h-[510px] flex-1", !isMounted && "opacity-0")}
      />
      {isMounted ? null : <TiptopCheckoutFormLoader />}
    </div>
  );
};

//

const TiptopCheckoutFormLoader: React.FC = () => {
  return (
    <div className="absolute inset-0 flex items-center justify-center">
      <Image alt="load-time" src="/images/load-time.gif" width={120} height={120} />
    </div>
  );
};
